/* eslint-disable camelcase */
import { getQueryParams } from '~/utils/urlHelpers';
import { LocalStorageCache } from './cache';
import {
  buildDiscoveryDocKey,
  DiscoveryDocument,
  isDiscoveryDocument,
  STALE_DISCOVERY_DOC_AGE,
} from './discovery';
import { buildUrlWithQueryParameters } from './functions';
import { PKCEVerifier } from './PKCEVerifier';

const DEFAULT_HEADERS = {
  Accept: 'application/json',
  'Content-Type': 'application/x-www-form-urlencoded',
};

export interface OpenIDConnectClientConfig {
  discoveryDocumentUrl: string;
  clientId: string;
  verifier: PKCEVerifier;
  scope: string[];
  redirectUri: string;
  defaultIdp?: string;
  allowRefreshTokensWithRotation?: boolean;
}

interface TokenParams {
  code: string;
}

export interface TokenResult {
  idToken: string;
  accessToken: string;
  refreshToken: string;
  /* refresh token expires in seconds */
  refreshExpiresIn: number;
  /* access token expires in seconds */
  expiresIn: number;
}

export interface RedirectResult {
  error?: string;
  isRedirectUri: boolean;
  returnTo?: string;
}

export const isTokenResult = (data: unknown): data is TokenResult => {
  const casted = data as TokenResult;
  const areStrings = typeof casted.accessToken === 'string' && typeof casted.idToken === 'string';
  const areNotEmpty = casted.accessToken.length > 0 && casted.idToken.length > 0;
  return areStrings && areNotEmpty;
};

export const getRedirectUri = ({
  location,
  redirectUri,
}: {
  location: string;
  redirectUri: string;
}): RedirectResult => {
  const startsWithRedirect = location.startsWith(redirectUri);
  if (!startsWithRedirect) {
    return {
      isRedirectUri: false,
    };
  }

  const queryParams = getQueryParams({ href: location, queryParamsToParse: ['state', 'code'] });

  const stateString = queryParams.state;
  const hasCode = queryParams.code !== undefined;
  let returnTo: string | undefined;
  if (stateString && hasCode) {
    const state = JSON.parse(atob(stateString));
    returnTo = state.returnTo;
  }
  return {
    isRedirectUri: startsWithRedirect,
    returnTo,
  };
};

/**
 * Implements PKCE Code Flow for OpenID Connect protocol
 *
 * @see https://auth0.com/docs/authorization/flows#authorization-code-flow-with-proof-key-for-code-exchange-
 *
 * Note: Don't use this client for Auth0. Even if Auth0 implements oidc, it is not fully compliant with the standard.
 * For example see https://github.com/IdentityModel/oidc-client-js/issues/1067
 *
 */
export class OpenIDConnectClient {
  /**
   * oidc discovery document
   */
  private discoveryDocument: DiscoveryDocument | null = null;

  /**
   * keep discovery document in local storage, it is public info
   */
  private cache = new LocalStorageCache();

  /**
   * keep id and access tokens in memory
   * Don't save them in session or local storage!
   */
  private tokenResult: TokenResult | undefined;

  /**
   * timeout handle if a token refresh is scheduled
   */
  private tokenRefreshTimeoutId: NodeJS.Timeout | undefined = undefined;

  // eslint-disable-next-line no-useless-constructor
  constructor(private config: OpenIDConnectClientConfig) {}

  public get isAuthenticated(): boolean {
    return this.tokenResult !== undefined;
  }

  public getAccessToken(): string | undefined {
    return this.tokenResult?.accessToken;
  }

  /**
   * Code generation phase in the PKCE Code Flow
   * @param returnTo relative path to Control Room UI
   */
  public async authorize({ returnTo }: { returnTo: string }) {
    const discoveryDocument = await this.ensureDiscoveryDocument();

    const codeChallenge = await this.config.verifier.generateCodeChallenge();
    const state = {
      returnTo,
    };
    const nonce = crypto.randomUUID();
    const baseUrl = discoveryDocument.authorization_endpoint;
    const parameters: Record<string, string> = {
      client_id: this.config.clientId,
      response_type: 'code',
      response_mode: 'query',
      scope: this.config.scope.join(' '),
      redirect_uri: this.config.redirectUri,
      state: btoa(JSON.stringify(state, undefined, 1)),
      nonce,
      code_challenge_method: 'S256',
      code_challenge: codeChallenge,
    };

    if (this.config.defaultIdp) {
      parameters.kc_idp_hint = this.config.defaultIdp;
    }

    const url = buildUrlWithQueryParameters(baseUrl, parameters);

    window.location.assign(url);
  }

  /**
   * Token exchange phase in the PKCE Code Flow
   * @param code
   * @returns
   */
  async exchangeCodeForTokens(code: string): Promise<TokenResult> {
    const discoveryDocument = await this.ensureDiscoveryDocument();

    const body = await this.buildTokenRequestBody({
      code,
    });

    const url = discoveryDocument.token_endpoint;

    const rawResult = await fetch(url, {
      headers: {
        ...DEFAULT_HEADERS,
      },
      body,
      method: 'POST',
    });

    if (rawResult.ok) {
      const json = await rawResult.json();
      const { id_token, access_token, refresh_token, refresh_expires_in, expires_in } = json;

      const result = {
        idToken: id_token,
        accessToken: access_token,
        refreshToken: refresh_token,
        refreshExpiresIn: refresh_expires_in,
        expiresIn: expires_in,
        issuedAt: Math.floor(Date.now() / 1000),
      };

      if (!isTokenResult(result)) {
        throw new Error('Malformed token response');
      }

      this.tokenResult = result;

      this.scheduleTokenRefresh();

      return result;
    }

    if (rawResult.status === 400) {
      //
      // XXX with Chrome/Chromium and Azure idp the token endpoint returns 400 the first time
      //     with Firefox works as expected
      //     how to reproduce: click on logout and then login again
      //     detailed message: "invalid_grant", "User session not found"
      //
      // The session is actually created on KeyCloak. So if we reload the page, it works.
      // This is clearly a hack and is not ideal, we should fix it
      // However, we spent already a lot of time on it.
      //
      // Some findings
      // 1. It could be related to stricter cookie policies in Chrome (there is a cookie with the session id). But the token endpoint does not require cookies.
      // 2. A race condition seems unlikely. We can pause before calling the token endpoint without success. Moreover we should hit the race condition also with Firefox.
      // 3. Nginx (reverse proxy) could also be involved, but no clue so far.
      // 4. It happens only with Azure IdP, Okta is fine
      //

      // eslint-disable-next-line no-console
      console.log(await rawResult.text());

      window.location.reload();

      return {} as any;
    }

    if (rawResult.status === 400) {
      //
      // XXX with Chrome/Chromium and Azure idp the token endpoint returns 400 the first time
      //     with Firefox works as expected
      //     how to reproduce: click on logout and then login again
      //     detailed message: "invalid_grant", "User session not found"
      //
      // The session is actually created on KeyCloak. So if we reload the page, it works.
      // This is clearly a hack and is not ideal, we should fix it
      // However, we spent already a lot of time on it.
      //
      // Some findings
      // 1. It could be related to stricter cookie policies in Chrome (there is a cookie with the session id). But the token endpoint does not require cookies.
      // 2. A race condition seems unlikely. We can pause before calling the token endpoint without success. Moreover we should hit the race condition also with Firefox.
      // 3. It happens only with Azure IdP, Okta is fine
      //

      // eslint-disable-next-line no-console
      console.log(await rawResult.text());

      window.location.reload();

      return {} as any;
    }

    throw new Error('Cannot fetch token');
  }

  /**
   * Refresh access token using a refresh token
   *
   * Your OpenID Connect client must enable refresh token with srotation.
   *
   * For Keycloak
   * 1. clients > your client > OpenID Connect Compatibility Modes > Use Refresh Tokens
   * 2. realm > tokens: Revoke Refresh Token ON, Refresh Token Max Reuse 0
   *
   * @see https://www.keycloak.org/docs/9.0/server_admin/#_timeouts
   */
  async refreshAccessToken(): Promise<TokenResult> {
    const discoveryDocument = await this.ensureDiscoveryDocument();

    if (!this.tokenResult?.refreshToken) {
      throw new Error('Missing refresh token');
    }

    const objectBody = {
      grant_type: 'refresh_token',
      client_id: this.config.clientId,
      refresh_token: this.tokenResult?.refreshToken,
    };

    const urlSearchParams = new URLSearchParams();
    // eslint-disable-next-line no-restricted-syntax
    for (const [key, value] of Object.entries(objectBody)) {
      urlSearchParams.set(key, value!);
    }

    const url = discoveryDocument.token_endpoint;

    const rawResult = await fetch(url, {
      headers: {
        ...DEFAULT_HEADERS,
      },
      body: urlSearchParams,
      method: 'POST',
    });

    if (rawResult.ok) {
      const json = await rawResult.json();
      const { id_token, access_token, refresh_token, refresh_expires_in, expires_in } = json;

      const result = {
        idToken: id_token,
        accessToken: access_token,
        refreshToken: refresh_token,
        refreshExpiresIn: refresh_expires_in,
        expiresIn: expires_in,
        issuedAt: Math.floor(Date.now() / 1000),
      };

      if (!isTokenResult(result)) {
        throw new Error('Malformed token response');
      }

      if (this.config.allowRefreshTokensWithRotation) {
        if (this.tokenResult.refreshToken === result.refreshToken) {
          // eslint-disable-next-line no-console
          console.warn('Refresh tokens are not rotated.');
        }
      }

      this.tokenResult = result;

      this.scheduleTokenRefresh();

      return result;
    }

    if (rawResult.status === 400) {
      const json = await rawResult.json();
      if (this.config.allowRefreshTokensWithRotation) {
        if (
          json.error === 'invalid_grant' &&
          json.error_description === 'Maximum allowed refresh token reuse exceeded'
        ) {
          // in this case the same token has been used more than once
          // hence the token may have be stolen
          // let's play safe: logout user from every session and ask to authenticate again
          const returnTo = `${window.location.protocol}//${window.location.host}/signout-callback`;
          await this.logout({ returnTo });
        }
      }
    }

    throw new Error('Cannot refresh access token');
  }

  async logout({ returnTo }: { returnTo: string }) {
    const discoveryDocument = await this.ensureDiscoveryDocument();

    if (!this.tokenResult) {
      // instead of skipping this, we throw an error
      // otherwise, session could be still active even if we don't have tokens locally
      throw new Error('Missing tokens');
    }

    const endSessionEndpoint = discoveryDocument.end_session_endpoint;
    const id_token_hint = this.tokenResult?.idToken;
    const logoutRedirect = returnTo;

    const params: Record<string, string> = {
      id_token_hint,
    };
    if (logoutRedirect) {
      // eslint-disable-next-line dot-notation
      params['post_logout_redirect_uri'] = logoutRedirect;
    }
    const url = buildUrlWithQueryParameters(endSessionEndpoint, params);

    // clean up local stuff
    window.sessionStorage.clear();
    this.tokenResult = undefined;

    // close session remotely
    window.location.assign(url);
  }

  private async fetchDiscoveryDocument(): Promise<DiscoveryDocument> {
    const res = await fetch(this.config.discoveryDocumentUrl).then((it) => it.json());
    if (!isDiscoveryDocument(res)) {
      throw new Error(`Cannot fetch discovery document: ${this.config.discoveryDocumentUrl}`);
    }
    return res;
  }

  private loadDiscoveryDocumentFromCache() {
    if (!window?.localStorage) {
      return null;
    }
    const rawDoc = this.cache.get(buildDiscoveryDocKey(this.config.clientId));
    if (!rawDoc) {
      return null;
    }
    const doc = JSON.parse(rawDoc);
    const now = Date.now();
    const age = now - (doc.timeStamp ?? 0);
    if (age > STALE_DISCOVERY_DOC_AGE) {
      return null;
    }

    delete doc.timeStamp;
    if (isDiscoveryDocument(doc)) {
      return doc;
    }
    return null;
  }

  private async ensureDiscoveryDocument() {
    if (!this.discoveryDocument) {
      let document = this.loadDiscoveryDocumentFromCache();
      if (!document) {
        document = await this.fetchDiscoveryDocument();
        this.saveDiscoveryDocument(document);
      }
      if (!document) {
        throw new Error('could not load discovery document');
      }

      this.discoveryDocument = document;
    }

    return this.discoveryDocument;
  }

  private saveDiscoveryDocument(document: DiscoveryDocument | null) {
    if (!document) {
      return;
    }
    const toSave = {
      timeStamp: Date.now().toString(),
      ...document,
    };

    this.cache.set(buildDiscoveryDocKey(this.config.clientId), JSON.stringify(toSave));
  }

  private async buildTokenRequestBody(params: TokenParams) {
    const objectBody = {
      grant_type: 'authorization_code',
      client_id: this.config.clientId,
      redirect_uri: this.config.redirectUri,
      code: params.code,
      code_verifier: await this.config.verifier.loadVerifier(),
    };

    const urlSearchParams = new URLSearchParams();
    // eslint-disable-next-line no-restricted-syntax
    for (const [key, value] of Object.entries(objectBody)) {
      urlSearchParams.set(key, value!);
    }
    return urlSearchParams;
  }

  private async scheduleTokenRefresh() {
    if (!this.config.allowRefreshTokensWithRotation) {
      return;
    }
    if (this.tokenRefreshTimeoutId) {
      clearTimeout(this.tokenRefreshTimeoutId);
    }
    if (this.tokenResult?.refreshToken) {
      const expiresIn = this.tokenResult.expiresIn || 3600;
      // refresh token when 80% of expiration time has passed
      const timeout = expiresIn * 0.8;
      this.tokenRefreshTimeoutId = setTimeout(async () => {
        await this.refreshAccessToken();
      }, timeout * 1000);
    }
  }
}
